import React, { Component, Fragment } from "react";
import { trackPromise } from "react-promise-tracker";
import { CSVLink } from "react-csv";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Card from "react-bootstrap/Card";
import { Formik } from "formik";
import Tour from "reactour";

import SliderTextInput from "../components/SliderTextInput";
import Select from "../components/Select";
import Switch from "../components/Switch";
import { Spinner } from "../components/spinner";
import ErrorMessage from "../components/ErrorMessage";
import NeurosphereService from "../services/NeurosphereService";

import { tourSteps } from "../utils/SimFieldContent";
import * as constants from "../utils/constants";
import * as val from "../utils/validation";

import legendBTSC from "../assets/images/Legend-Key-BTSC.jpg";
import legendNoBTSC from "../assets/images/Legend-Key-No-BTSC.jpg";
import dataDownloadDescription from "../assets/images/Data_download_description.pdf";

const defaultParams = {
  PM_min: 0.5,
  PM_max: 1,
  PD_min: 0.05,
  PD_max: 0.5,
  PA: 1,
  N_quiescent: 1,
  N_proliferative: 7,
  N_death: 5,
  show_BTSC: false,
  t_transformation: 10,
  init_cell_type: "Active stem cell",
  grid_type: "Square Lattice",
  lattice_size: "Medium",
}

class NeurosphereContainer extends Component {
  constructor(props) {
    super(props);

    this.state = {
      params: defaultParams,
      cellTypes: ["Active stem cell", "Progenitor cell"],
      gridTypes: ["Square Lattice", "Hexagon Lattice"],
      latticeSizes: ["Medium", "Small", "Large"],
      simVideo: "",
      simData: "",
      errMsg: "",
      steps: tourSteps,
      isTourOpen: false,
    };
    this.handleFormSubmit = this.handleFormSubmit.bind(this);
  }
  componentDidMount() {
    this.focusedItem.focus();
  }
  closeTour() {
    this.setState({ isTourOpen: false });
  }

  openTour() {
    this.setState({ isTourOpen: true });
  }

  handleFormSubmit(paramSelection) {
    trackPromise(
      NeurosphereService.requestSim(
        paramSelection,

        (response) => {
          this.setState({
            simVideo: response["video"],
            simData: response["data"],
            params: paramSelection,
            errMsg: "",
          });
        },
        (errMsg) => {
          this.setState({
            errMsg: errMsg["message"],
            simVideo: "",
            simData: "",
          });
        }
      )
    );
  }

  render() {
    return (
      <div className="col">
        <Tour
          steps={this.state.steps}
          isOpen={this.state.isTourOpen}
          onRequestClose={() => this.closeTour()}
          ariaLabelledBy="data-tour"
          showNavigationNumber={true}
          showNumber={true}
          closeButtonAriaLabel={"Close"}
          howNavigationScreenReaders={true}
          accessibilityOptions={{
            ariaLabelledBy: "data-tour",
            closeButtonAriaLabel: "Close",
            showNavigationScreenReaders: true,
          }}
        />
        <div className="row my-3 align-items-center">
          <div className="px-2">
            <i>
              If this is your first time using the Neurosphere Simulator, click
              here:
              </i>
          </div>
          <Button
            variant="secondary"
            onClick={() => this.openTour()}
            ref={(focus) => {
              this.focusedItem = focus;
            }}
          >
            Take a tour!
            </Button>
        </div>
        <Formik
          validationSchema={val.PARAM_VALIDATION}
          onSubmit={this.handleFormSubmit}
          initialValues={this.state.params}
        >
          {({ handleSubmit, handleChange, values, isValid, errors, resetForm }) => (
            <Form noValidate onSubmit={handleSubmit} data-tour="3">
              <Row xs={2} md={4}>
                <Col>
                  <Select
                    title="Lattice Type"
                    name="grid_type"
                    options={this.state.gridTypes}
                    placeholder="Select a lattice type"
                    datatour="7"
                  />
                </Col>
                <Col>
                  <Select
                    title="Lattice Size"
                    name="lattice_size"
                    options={this.state.latticeSizes}
                    placeholder="Select the animation lattice size"
                    datatour="4"
                  />
                </Col>
                <Col>
                  <Select
                    title="Founder Cell Type"
                    name="init_cell_type"
                    options={this.state.cellTypes}
                    placeholder="Select the initial cell type"
                  />
                </Col>
                {constants.PARAM_CONFIG.map((param) => {
                  return param.name === "t_transformation" ? (
                    <Fragment>
                      <Col>
                        <Switch
                          title="Transformation to Brain Tumor Stem Cell (BTSC)"
                          name="show_BTSC"
                          key="show_BTSC"
                          desc="Allow BTSC Transformation"
                          datatour="6"
                        />
                      </Col>
                      {values["show_BTSC"] ? (
                        <Col>
                          <SliderTextInput
                            type={param.type}
                            title={param.title}
                            name={param.name}
                            key={param.name}
                            desc={param.desc}
                            min={param.min}
                            max={param.max}
                            step={param.step}
                          />
                        </Col>
                      ) : null}
                      {values["show_BTSC"] ? (
                        <Col>
                          <Card style={{ width: "18rem" }}>
                            <Card.Body className="d-flex flex-column">
                              <Card.Title style={{ color: "red" }}>
                                Warning:
                              </Card.Title>
                              <Card.Text>
                                It may be the case that, although being enabled,
                                no transformation of a progenitor to a BTSC
                                takes place due to the absence of progenitor
                                cells at the time instant of transformation.
                                However, since the model is stochastic,
                                rerunning the simulation may yield an outcome
                                with BTSC transformation.
                              </Card.Text>
                            </Card.Body>
                          </Card>
                        </Col>
                      ) : null}
                    </Fragment>
                  ) : (
                      <Col>
                        <SliderTextInput
                          type={param.type}
                          title={param.title}
                          name={param.name}
                          key={param.name}
                          desc={param.desc}
                          min={param.min}
                          max={param.max}
                          step={param.step}
                        />
                      </Col>
                    );
                })}
              </Row>
              <Button
                style={{ marginBottom: "1rem" }}
                variant="secondary"
                onClick={() => resetForm()}
              >
                Reset default parameters
              </Button>
              <div className="row">
                <div className="col-md-2">
                  <Button
                    disabled={!isValid}
                    variant="primary"
                    type="submit"
                    data-tour="9"
                    onClick={() =>
                      this.setState({
                        simVideo: "",
                        simData: "",
                      })
                    }
                  >
                    {" "}
                    Run Model{" "}
                  </Button>
                </div>
              </div>
            </Form>
          )}
        </Formik>
        <Spinner>
          {this.state.simVideo !== "" ? (
            <Card
              className="mt-3 align-items-center"
              style={{ width: "75rem" }}
            >
              <div dangerouslySetInnerHTML={{ __html: this.state.simVideo }} />
              <div className="row my-3">
                {this.state.params.show_BTSC ||
                  this.state.params.init_cell_type === "BTSC" ? (
                    <img
                      src={legendBTSC}
                      alt="Legend"
                      style={{ height: "25vh", width: "auto" }}
                    />
                  ) : (
                    <img
                      src={legendNoBTSC}
                      alt="Legend"
                      style={{ height: "25vh", width: "auto" }}
                    />
                  )}
                {this.state.simData !== "" && (
                  <div className="col-md-4 mx-2">
                    <div className="mb-2">
                      <CSVLink
                        data={this.state.simData}
                        filename={"MQD_Data.csv"}
                      >
                        <Button variant="primary"> Download MQD Data </Button>
                      </CSVLink>
                    </div>
                    <div>
                      <a
                        href={dataDownloadDescription}
                        download="Data_download_description.pdf"
                      >
                        <Button variant="primary">
                          Download MQD Description
                        </Button>
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </Card>
          ) : null}
          {this.state.errMsg ? (
            <ErrorMessage
              role="alert"
              content={
                "An error occurred when running the model: " + this.state.errMsg
              }
            />
          ) : null}
        </Spinner>
      </div >
    );
  }
}

export default NeurosphereContainer;
